import {WorksitesMapProps} from "./types";
import {ScatterMap} from "@components/Maps/ScatterMap";
import React, {useCallback, useContext, useEffect, useState} from "react";
import AuthenticationService from "@services/AuthenticationService";
import {useIntl} from "react-intl";
import {useHomeWorksites} from "@feature/worksites/home/hooks/useHomeWorksites";
import {WorksitesPin} from "@feature/worksites/home/types";
import {BlurredPlaceholder} from "@feature/commons/blurred-placeholder";
import CountryContext from "../../../../CountryContext";
import {getBoundingBox} from "./lib/utils";
import {DEFAULT_VIEWPORT} from "@feature/worksites/home/map/lib/consts";
import _ from "lodash";


export const WorksitesMap = ({filters, onClick, refreshObservable}: WorksitesMapProps) => {

  // context
  const {country: {code}} = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const {loading, fetchPins: fetch} = useHomeWorksites();

  // state
  const [data, setData] = useState<WorksitesPin[]>();
  const [boundingBox, setBoundingBox]
    = useState<{rng_latitude: [number, number], rng_longitude: [number, number]}>(
      getBoundingBox(
        [DEFAULT_VIEWPORT[code].longitude, DEFAULT_VIEWPORT[code].latitude],
        DEFAULT_VIEWPORT[code].zoom,
        0,
        0,
      )
    );

  // utils
  const userData = AuthenticationService.getUserData();
  const LegendItem = ({color, text}: {color: string, text: string}) => (
    <div className="flex flex-row gap-2 items-center">
      <div className={`rounded-full w-4 h-4 ${color}`} />
      <span className="leading-tight">{text}</span>
    </div>
  )
  const generateLegendText = (target: string) => intl.formatMessage(
    {id: "shared_with_plural", defaultMessage: "Condivisi {target}"},
    {target}
  )
  const debouncedFetch = useCallback(
    _.debounce((filters, boundingBox, setData) => {
      fetch(filters, boundingBox).then(setData);
    }, 500),
    []  // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    // On initial load and on filters/box change, we need to (re)fetch the pins
    debouncedFetch(filters, boundingBox, setData);
  }, [JSON.stringify(filters), boundingBox, refreshObservable]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4 h-96 sm:h-full">
      <div className="flex-grow">
        <BlurredPlaceholder loading={loading}>
          <ScatterMap
            id="worksite-scatter-map"
            data={data}
            onClick={onClick}
            onNavigation={(viewState) => {
              const {width, height, zoom, latitude, longitude} = viewState;
              const bbox = getBoundingBox([longitude, latitude], zoom, width, height);
              setBoundingBox(bbox);
            }}
            draggable={true}
          />
        </BlurredPlaceholder>
      </div>
      <div className="flex flex-wrap gap-5 left-10">
        <LegendItem color="bg-kerakoll-700" text={generateLegendText("Kerakoll")} />
        {userData.groups.find((e: any) => e === "Knauf") && (
          <LegendItem color="bg-white border-4 border-knauf-700" text={generateLegendText("Knauf")} />
        )}
        <LegendItem color="bg-gray-900" text={intl.formatMessage({id: "my_worksites"})} />
      </div>
    </div>
  )
}